/**
 * Main Game component - orchestrates all game systems
 */

import { useEffect, useState, useCallback } from 'react';
import { GameCanvas } from './GameCanvas';
import { GameHUD } from './GameHUD';
import { GameMenu } from './GameMenu';
import { GameOver } from './GameOver';
import { useGameState } from '../hooks/useGameState';
import { useTimer } from '../hooks/useTimer';
import { useParticles } from '../hooks/useParticles';
import { useCanvas } from '../hooks/useCanvas';
import { generateShape, calculateDistance, getShapeCenter, isClickInShape } from '../gameLogic/shapes';
import { calculateClickResult } from '../gameLogic/scoring';
import { calculateTimerDuration, calculateStrikeThreshold } from '../gameLogic/difficulty';
import {
  emitClickParticles,
  emitCenterParticles,
  emitShapeOutlineParticles,
} from '../particles/ParticleEmitter';
import {
  MAX_STRIKES,
  LEVEL_TRANSITION_DELAY_MS,
  CLICK_PARTICLE_COLOR,
} from '../utils/constants';
import { analytics } from '../analytics/gameAnalytics';
import { useChaos } from '../../shared/chaos/useChaos';

export function Game() {
  const { incrementChaos } = useChaos();

  // Error boundary - catch and log errors
  useEffect(() => {
    const errorHandler = (event: ErrorEvent) => {
      analytics.logError(event.message, event.filename);
    };
    window.addEventListener('error', errorHandler);
    return () => window.removeEventListener('error', errorHandler);
  }, []);

  const {
    gameState,
    stats,
    currentShape,
    settings,
    isAnimating,
    startGame,
    endGame,
    nextLevel,
    setCurrentShape,
    addScore,
    addStrike,
    setAnimating,
    resetGame,
  } = useGameState();

  const { dimensions } = useCanvas();
  const { particlesRef, addParticles, clearParticles } = useParticles();
  const { progress, startTimer, resetTimer } = useTimer(handleTimeout);

  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackAlpha, setFeedbackAlpha] = useState(0);

  // Feedback text fade out
  useEffect(() => {
    if (feedbackText) {
      setFeedbackAlpha(1);
      const fadeInterval = setInterval(() => {
        setFeedbackAlpha(prev => {
          const newAlpha = prev - 0.02;
          if (newAlpha <= 0) {
            clearInterval(fadeInterval);
            setFeedbackText('');
            return 0;
          }
          return newAlpha;
        });
      }, 16);

      return () => clearInterval(fadeInterval);
    }
  }, [feedbackText]);

  // Start new level
  const triggerNextLevel = useCallback(() => {
    if (dimensions.width === 0 || dimensions.height === 0) return;

    setAnimating(true);
    setCurrentShape(null);

    setTimeout(() => {
      const newShape = generateShape(dimensions.width, dimensions.height);
      setCurrentShape(newShape);
      setAnimating(false);

      const timerDuration = calculateTimerDuration(stats.level);
      startTimer(timerDuration);
    }, LEVEL_TRANSITION_DELAY_MS);
  }, [dimensions, stats.level, setCurrentShape, setAnimating, startTimer]);

  // Handle game start
  const handleStart = useCallback(() => {
    startGame();
    clearParticles();
    triggerNextLevel();
    analytics.startSession();
  }, [startGame, clearParticles, triggerNextLevel]);

  // Handle timeout
  function handleTimeout() {
    if (gameState !== 'playing') return;

    addStrike();

    if (stats.strikes + 1 >= MAX_STRIKES) {
      const isNewHigh = stats.score > 0 && stats.score === stats.highScore;
      analytics.endSession(stats.score, stats.level, stats.strikes + 1, isNewHigh);
      incrementChaos(5);
      endGame();
      resetTimer();
    } else {
      nextLevel();
      triggerNextLevel();
    }
  }

  // Handle canvas click
  const handleCanvasClick = useCallback((x: number, y: number) => {
    if (gameState !== 'playing' || !currentShape || isAnimating) return;

    // Check if click is within shape bounds
    if (!isClickInShape(x, y, currentShape)) {
      return;
    }

    const center = getShapeCenter(currentShape);
    const distance = calculateDistance(x, y, center.x, center.y);
    const strikeThreshold = calculateStrikeThreshold(stats.level);
    const result = calculateClickResult(distance, stats.level, strikeThreshold);

    // Track click
    const isPerfect = distance < 5; // Within 5px of center
    analytics.trackClick(isPerfect);

    // Add score
    addScore(result.points);

    // Show feedback
    if (settings.showMotivation && result.feedback) {
      setFeedbackText(result.feedback);
    }

    // Create particles
    const clickParticles = emitClickParticles(x, y, CLICK_PARTICLE_COLOR);
    const centerParticles = emitCenterParticles(center.x, center.y, currentShape.color);
    const outlineParticles = emitShapeOutlineParticles(currentShape);

    addParticles([...clickParticles, ...centerParticles, ...outlineParticles]);

    // Handle strike
    if (result.isStrike) {
      addStrike();

      if (stats.strikes + 1 >= MAX_STRIKES) {
        const isNewHigh = stats.score + result.points > 0 && stats.score + result.points === stats.highScore;
        analytics.endSession(stats.score + result.points, stats.level, stats.strikes + 1, isNewHigh);
        incrementChaos(5);
        endGame();
        resetTimer();
        return;
      }
    }

    // Progress to next level
    nextLevel();
    triggerNextLevel();
  }, [
    gameState,
    currentShape,
    isAnimating,
    stats.level,
    stats.strikes,
    settings.showMotivation,
    addScore,
    addStrike,
    addParticles,
    nextLevel,
    endGame,
    resetTimer,
    triggerNextLevel,
  ]);

  // Handle restart
  const handleRestart = useCallback(() => {
    resetGame();
    clearParticles();
  }, [resetGame, clearParticles]);

  const isNewHighScore = stats.score > 0 && stats.score === stats.highScore && gameState === 'gameOver';

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <GameCanvas
        shape={currentShape}
        particlesRef={particlesRef}
        progress={progress}
        feedbackText={feedbackText}
        feedbackAlpha={feedbackAlpha}
        onCanvasClick={handleCanvasClick}
      />

      {gameState === 'playing' && <GameHUD stats={stats} />}

      {gameState === 'menu' && (
        <GameMenu highScore={stats.highScore} onStart={handleStart} />
      )}

      {gameState === 'gameOver' && (
        <GameOver
          score={stats.score}
          level={stats.level}
          highScore={stats.highScore}
          isNewHighScore={isNewHighScore}
          onRestart={handleRestart}
        />
      )}
    </div>
  );
}
